import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  ArrowUpRight,
  Sparkles,
  PenLine,
  Microscope,
  Wand2,
  Bot,
  Calendar,
  Mail,
  Linkedin,
  Twitter,
  ArrowDown,
  CheckCircle2,
  Zap,
  Quote,
} from "lucide-react";

const CALENDLY = "https://calendly.com/mehakvishwakarma3";
const EMAIL = "mehakvishwakarma3@gmail.com";
const LINKEDIN = "https://www.linkedin.com/in/mehak-vishwakarma-bba70a285/";
const X_URL = "https://x.com/mehak_karma";

const ASSETS = {
  thf: "https://customer-assets.emergentagent.com/job_727aab79-4ef0-406d-8852-9c93ef980396/artifacts/8fe7mbtg_m1.PNG",
  leadHunter:
    "https://customer-assets.emergentagent.com/job_727aab79-4ef0-406d-8852-9c93ef980396/artifacts/eq9rcfpv_m2.PNG",
  career:
    "https://customer-assets.emergentagent.com/job_727aab79-4ef0-406d-8852-9c93ef980396/artifacts/cohkotfp_m4.PNG",
  newsletter:
    "https://customer-assets.emergentagent.com/job_727aab79-4ef0-406d-8852-9c93ef980396/artifacts/o1cay18p_m5.PNG",
};

/* ---------- Top Nav (sits inside hero card) ---------- */
const Nav = () => (
  <div className="relative z-30 px-6 md:px-10 pt-6 md:pt-7" data-testid="site-nav">
    <div className="flex items-center justify-between">
      <a href="#top" className="flex items-center gap-2 text-white" data-testid="brand-logo">
        <span className="h-8 w-8 rounded-full bg-[var(--lime)] grid place-items-center text-[var(--ink)] font-black">
          M
        </span>
        <span className="font-display text-xl font-medium tracking-tight">Mehak</span>
      </a>

      <nav className="hidden md:flex items-center gap-8 text-white/85 text-sm font-light">
        <a href="#services" className="link-under" data-testid="nav-services">Services</a>
        <a href="#work" className="link-under" data-testid="nav-work">Selected Work</a>
        <a href="#about" className="link-under" data-testid="nav-about">About</a>
        <a href="#contact" className="link-under" data-testid="nav-contact">Contact</a>
      </nav>

      <a
        href={CALENDLY}
        target="_blank"
        rel="noreferrer"
        className="pill-lime inline-flex items-center gap-2 px-5 py-2.5 text-sm"
        data-testid="nav-book-call-btn"
      >
        Book a Call
        <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  </div>
);

/* ---------- Hero ---------- */
const Hero = () => (
  <section id="top" className="bg-[var(--paper)] pt-4 md:pt-5 px-3 md:px-5" data-testid="hero-section">
    <div className="hero-sky relative overflow-hidden">
      <div className="hero-grain" />
      <Nav />

      <div className="relative z-10 text-center text-white px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="rise rise-1 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[11px] tracking-[0.16em] uppercase mb-7 font-light">
          <Sparkles className="h-3.5 w-3.5 text-[var(--lime)]" />
          Founder's operator. Not an AI mill.
        </div>

        <h1
          className="rise rise-2 font-display leading-[0.98] text-[44px] sm:text-6xl lg:text-7xl xl:text-[84px] max-w-5xl mx-auto"
          data-testid="hero-headline"
        >
          <span className="font-medium">I help founders</span>{" "}
          <span className="font-serif-italic font-thin text-[var(--lime)]">cut noise,</span>
          <br className="hidden sm:block" />
          <span className="font-medium">build presence</span>{" "}
          <span className="font-serif-italic font-thin text-[var(--lime)]">&amp; ship faster.</span>
        </h1>

        <p
          className="rise rise-3 mt-8 text-[15px] sm:text-[17px] text-white/85 max-w-2xl mx-auto leading-relaxed font-light"
          data-testid="hero-subtext"
        >
          $80K+ added to client pipelines from landing pages.
          <br className="hidden sm:block" />
          25 founder hours saved every month with custom AI tools.
          <br className="hidden sm:block" />
          0 to 8,500 newsletter readers. Leverage, not slop.
        </p>

        <div className="rise rise-4 mt-10 flex items-center justify-center gap-3 flex-wrap">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noreferrer"
            className="pill-lime inline-flex items-center gap-2 px-7 py-4 text-[15px]"
            data-testid="hero-book-call-btn"
          >
            Book a Call
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href="#services"
            className="pill-ghost inline-flex items-center gap-2 px-7 py-4 text-[15px] font-light"
            data-testid="hero-see-work-btn"
          >
            See the work
            <ArrowDown className="h-4 w-4" />
          </a>
        </div>

        <div className="rise rise-5 mt-12 flex items-center justify-center gap-x-6 gap-y-3 text-xs text-white/70 flex-wrap font-light">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--lime)]" />
            $80K+ booked pipeline
          </div>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--lime)]" />
            0 to 8.5K newsletter
          </div>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--lime)]" />
            25+ founder hours saved
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Marquee strip ---------- */
const Marquee = () => {
  const items = [
    "Founder positioning",
    "LinkedIn that doesn't read like ChatGPT",
    "Lead-gen funnels",
    "Custom AI agents",
    "Market & competitor research",
    "Newsletters that ship",
    "Brand voice systems",
    "Landing pages that convert",
  ];
  const full = [...items, ...items];
  return (
    <div className="bg-[var(--paper)] py-5 overflow-hidden mt-5" data-testid="marquee">
      <div className="border-y border-[var(--ink)]/10 py-4 overflow-hidden">
        <div className="marquee">
          {full.map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-6 px-6 text-[var(--ink)]/70 text-sm whitespace-nowrap"
            >
              <Sparkles className="h-3.5 w-3.5 text-[var(--blue)]" />
              <span className="font-medium">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- Services Tiles ---------- */
const ServiceTile = ({ variant, label, title, body, bullets, stat, icon: Icon, testid, className = "" }) => {
  const variants = {
    lime: "tile-lime",
    ink: "tile-ink",
    paper: "tile-paper",
    stone: "tile-stone",
  };
  return (
    <div className={`tile ${variants[variant]} p-7 md:p-9 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-8">
        <span
          className={`chip ${
            variant === "ink"
              ? "bg-white/10 text-white"
              : variant === "lime"
              ? "bg-[var(--ink)] text-[var(--lime)]"
              : "bg-[var(--ink)]/8 text-[var(--ink)]"
          }`}
        >
          {label}
        </span>
        <div
          className={`h-10 w-10 rounded-full grid place-items-center ${
            variant === "ink"
              ? "bg-[var(--lime)] text-[var(--ink)]"
              : variant === "lime"
              ? "bg-[var(--ink)] text-[var(--lime)]"
              : "bg-[var(--ink)] text-white"
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>

      <h3 className="font-display font-medium text-2xl md:text-[28px] leading-tight mb-4">
        {title}
      </h3>
      <p
        className={`text-[15px] leading-relaxed mb-6 font-light ${
          variant === "ink" ? "text-white/75" : "text-[var(--ink)]/70"
        }`}
      >
        {body}
      </p>

      <ul className="space-y-2.5 mb-7">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span
              className={`mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                variant === "ink" ? "bg-[var(--lime)]" : "bg-[var(--ink)]"
              }`}
            />
            <span className={variant === "ink" ? "text-white/85" : ""}>{b}</span>
          </li>
        ))}
      </ul>

      <div
        className={`pt-5 border-t ${
          variant === "ink" ? "border-white/10" : "border-[var(--ink)]/10"
        } flex items-baseline justify-between`}
      >
        <div>
          <div
            className={`font-display font-medium text-3xl md:text-4xl ${
              variant === "lime" ? "text-[var(--ink)]" : ""
            }`}
          >
            {stat.value}
          </div>
          <div
            className={`text-xs mt-1 font-light ${
              variant === "ink" ? "text-white/55" : "text-[var(--ink)]/55"
            }`}
          >
            {stat.label}
          </div>
        </div>
        <a
          href={CALENDLY}
          target="_blank"
          rel="noreferrer"
          className={`inline-flex items-center gap-1 text-sm font-medium link-under ${
            variant === "ink" ? "text-[var(--lime)]" : ""
          }`}
          data-testid={`${testid}-cta`}
        >
          Book a call <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};

const Services = () => (
  <section id="services" className="bg-[var(--paper)] py-24 md:py-32" data-testid="services-section">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink)]/55 mb-4 font-medium">
            · Services
          </div>
          <h2 className="font-display font-medium text-4xl md:text-6xl leading-[1.02] max-w-3xl">
            Four ways I make founders
            <br />
            <span className="font-serif-italic font-thin text-[var(--blue)]">
              actually unblockable.
            </span>
          </h2>
        </div>
        <p className="text-[var(--ink)]/65 max-w-sm text-[15px] leading-relaxed font-light">
          Pick one. Pick all four. Every engagement ends with you owning the system, not paying me forever.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7">
          <ServiceTile
            variant="ink"
            label="01 · Content"
            icon={PenLine}
            title="Human content machine. No AI garbage."
            body="Founder-voice writing that sounds like you on your sharpest day. Built from research and reps, not recycled prompts. The kind of content that compounds attention and trust."
            bullets={[
              "Grew a creator from 0 to 35K in 30 days",
              "Helped build an 8,500+ subscriber newsletter",
              "200,000+ newsletter views across published essays",
            ]}
            stat={{ value: "0 → 35K", label: "creator grown in 30 days" }}
            testid="tile-content"
            className="h-full"
          />
        </div>

        <div className="lg:col-span-5">
          <ServiceTile
            variant="lime"
            label="02 · Research"
            icon={Microscope}
            title="Researcher and strategist on call."
            body="Markets, competitors, customers, ideas. I dig where most agencies skim, then turn it into a one-page strategy you can actually run on Monday morning."
            bullets={[
              "2 books published, research featured on SSRN",
              "Built a 1,000+ institutional audience from zero",
              "Cross-disciplinary: economics, behaviour, AI, policy",
            ]}
            stat={{ value: "1,000+", label: "institutional readers built from zero" }}
            testid="tile-research"
            className="h-full"
          />
        </div>

        <div className="lg:col-span-5">
          <ServiceTile
            variant="stone"
            label="03 · Branding & Sites"
            icon={Wand2}
            title="Brand, landing pages and funnels that book calls."
            body="Founder positioning, narrative, and high-intent landing pages. Built so the right people read it, get it, and hit your calendar without you chasing."
            bullets={[
              "$80,000+ added to a client's sales-call pipeline",
              "+20 booked calls a month for a coaching client",
              "Helped book 10 to 15 extra calls every month",
            ]}
            stat={{ value: "$80K+", label: "added in booked sales pipeline" }}
            testid="tile-branding"
            className="h-full"
          />
        </div>

        <div className="lg:col-span-7">
          <ServiceTile
            variant="paper"
            label="04 · Custom AI Tools"
            icon={Bot}
            title="Custom AI tools that buy you back your time."
            body="Lean, founder-grade AI built for your workflow. Lead scrapers, engagement bots, research copilots, internal agents. The boring repeatable work disappears so you ship the work that compounds."
            bullets={[
              "Built an AI lead hunter that saved 25 hrs/month on outreach",
              "Built an Instagram engagement bot for a creator-investor",
              "AI strategy + custom workflow automations end-to-end",
            ]}
            stat={{ value: "~25 hrs", label: "founder time saved every month" }}
            testid="tile-ai"
            className="h-full"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Selected Work ---------- */
const WorkCard = ({ tag, title, body, kpi, img, testid, link }) => (
  <a
    href={link || CALENDLY}
    target="_blank"
    rel="noreferrer"
    className="group block tile tile-paper p-5"
    data-testid={testid}
  >
    <div className="relative rounded-2xl overflow-hidden bg-[var(--paper-warm)] aspect-[16/10]">
      <img
        src={img}
        alt={title}
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
      <div className="absolute top-3 left-3">
        <span className="chip bg-white/95 backdrop-blur text-[var(--ink)]">{tag}</span>
      </div>
      {kpi ? (
        <div className="absolute bottom-3 right-3">
          <span className="chip bg-[var(--ink)] text-[var(--lime)]">{kpi}</span>
        </div>
      ) : null}
    </div>
    <div className="p-3 pt-5">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-display font-medium text-xl leading-snug">{title}</h3>
        <ArrowUpRight className="h-5 w-5 text-[var(--ink)]/50 group-hover:text-[var(--blue)] transition-colors flex-shrink-0 mt-1" />
      </div>
      <p className="text-sm text-[var(--ink)]/65 mt-2 leading-relaxed font-light">{body}</p>
    </div>
  </a>
);

const Work = () => (
  <section
    id="work"
    className="bg-[var(--paper-warm)] py-24 md:py-32 border-y border-[var(--ink)]/8"
    data-testid="work-section"
  >
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink)]/55 mb-4 font-medium">
            · Selected Work
          </div>
          <h2 className="font-display font-medium text-4xl md:text-6xl leading-[1.02] max-w-3xl">
            Real builds.{" "}
            <span className="font-serif-italic font-thin text-[var(--blue)]">Real founders.</span>
          </h2>
        </div>
        <p className="text-[var(--ink)]/65 max-w-sm text-[15px] leading-relaxed font-light">
          A snapshot of recent work. Funnels, AI tools, SaaS interfaces and editorial systems shipped for founders and operators.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <WorkCard
          tag="Landing Page · Funnel"
          kpi="+$80K pipeline"
          title="THF Consultants. Surplus funds recovery site."
          body="High-intent landing page for an asset recovery firm. Live counters, trust signals and a single conversion path. Added $80,000+ in booked sales-call pipeline."
          img={ASSETS.thf}
          testid="work-thf"
        />
        <WorkCard
          tag="Custom AI Tool"
          kpi="25 hrs saved · +$15K pipeline"
          title="Lead Hunter. AI business email scraper."
          body="Custom AI tool for a founder. Expands niches into synonyms, scrapes Maps and the web, returns enriched leads in minutes. Saved 25+ hours of outreach a month and added ~$15K in sales pipeline."
          img={ASSETS.leadHunter}
          testid="work-leadhunter"
        />
        <WorkCard
          tag="SaaS · Recruiting"
          kpi="AI counseling SaaS"
          title="Career Counselor. AI personality reports."
          body="Designed and shaped the founder-facing report flow for a recruiting agency's AI counseling product. Identity, personality and self-fit scoring in one clean read."
          img={ASSETS.career}
          testid="work-career"
        />
        <WorkCard
          tag="Newsletter Growth"
          kpi="0 → 8,511 subs"
          title="0 to 8,500 newsletter readers."
          body="Helped a founder grow a Substack newsletter from a cold start to 8,500+ engaged subscribers with editorial systems and distribution."
          img={ASSETS.newsletter}
          testid="work-newsletter"
        />
      </div>

      <div className="mt-14 flex items-center justify-center">
        <a
          href={CALENDLY}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--ink)] text-white text-sm font-medium hover:bg-[var(--ink-soft)] transition-colors"
          data-testid="work-book-call-btn"
        >
          Want something like this? Book a call
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  </section>
);

/* ---------- About ---------- */
const StatCard = ({ value, label, variant = "white", testid }) => {
  const styles = {
    white: "bg-white border border-[var(--ink)]/8 text-[var(--ink)]",
    ink: "bg-[var(--ink)] text-white",
    lime: "bg-[var(--lime)] text-[var(--ink)]",
    stone: "bg-[var(--paper-warm)] text-[var(--ink)]",
  };
  const sub = variant === "ink" ? "text-white/65" : "text-[var(--ink)]/60";
  const big = variant === "ink" ? "text-[var(--lime)]" : "";
  return (
    <div className={`p-5 rounded-2xl ${styles[variant]}`} data-testid={testid}>
      <div className={`font-display text-3xl md:text-4xl font-medium ${big}`}>{value}</div>
      <div className={`text-xs mt-2 font-light ${sub}`}>{label}</div>
    </div>
  );
};

const About = () => (
  <section id="about" className="bg-[var(--paper)] py-24 md:py-32" data-testid="about-section">
    <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink)]/55 mb-4 font-medium">
          · About
        </div>
        <h2 className="font-display font-medium text-4xl md:text-5xl leading-[1.05]">
          Research led. Human written.{" "}
          <span className="font-serif-italic font-thin text-[var(--blue)]">
            Built for founders who refuse generic.
          </span>
        </h2>

        <div className="mt-10 grid grid-cols-2 gap-4">
          <StatCard value="$80K+" label="added to client sales pipelines" variant="ink" testid="stat-pipeline" />
          <StatCard value="+20/mo" label="extra calls booked for a coaching client" variant="lime" testid="stat-calls" />
          <StatCard value="25 hrs" label="founder time saved every month with AI" variant="white" testid="stat-hours" />
          <StatCard value="8,500+" label="newsletter subscribers grown" variant="stone" testid="stat-subs" />
          <StatCard value="3,000+" label="LinkedIn followers grown organically" variant="white" testid="stat-linkedin" />
          <StatCard value="200K+" label="newsletter views across essays" variant="stone" testid="stat-views" />
        </div>
      </div>

      <div className="lg:col-span-7">
        <p className="text-lg md:text-xl text-[var(--ink)]/85 leading-relaxed">
          I'm Mehak. A researcher, writer and operator working with founders who are tired of generic agencies, AI slop content, and strategy decks that go nowhere.
        </p>
        <p className="text-[15px] text-[var(--ink)]/65 mt-5 leading-relaxed font-light">
          My background is research. Published papers, two books, policy briefs. I bring that rigor into branding, content and AI tooling. Every project starts with a real question: <em>what's the gap between how you're positioned and how you're perceived?</em> Then we close it with words, systems and software that actually ship.
        </p>
        <p className="text-[15px] text-[var(--ink)]/65 mt-4 leading-relaxed font-light">
          1,000+ institutional readers built from zero. 3,000+ LinkedIn followers grown organically. 200,000+ newsletter views across published essays. The work compounds because it's done with care, not prompts.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { k: "01", t: "Observe", d: "Where positioning and perception don't match." },
            { k: "02", t: "Diagnose", d: "Why it's happening, in plain English." },
            { k: "03", t: "Build", d: "Content, brand and AI tools to close the gap." },
            { k: "04", t: "Hand off", d: "You own the system. I don't hold it hostage." },
          ].map((s) => (
            <div
              key={s.k}
              className="p-5 rounded-2xl bg-white border border-[var(--ink)]/8"
              data-testid={`process-${s.k}`}
            >
              <div className="text-xs text-[var(--blue)] font-semibold tracking-wider">{s.k}</div>
              <div className="font-display font-medium text-xl mt-1">{s.t}</div>
              <div className="text-sm text-[var(--ink)]/60 mt-1.5 font-light">{s.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Pull quote ---------- */
const Pull = () => (
  <section className="bg-[var(--ink)] text-white py-24 md:py-28" data-testid="pull-quote">
    <div className="container-x">
      <div className="max-w-4xl">
        <Quote className="h-10 w-10 text-[var(--lime)] mb-6" />
        <p className="font-display text-3xl md:text-5xl leading-[1.1] font-light">
          "Most strategy problems begin as{" "}
          <span className="font-serif-italic font-thin text-[var(--lime)]">observation problems.</span>{" "}
          I look for the gap between how a founder is positioned and how they're actually being read."
        </p>
        <div className="mt-8 flex items-center gap-3 text-white/70 text-sm font-light">
          <span className="h-8 w-8 rounded-full bg-[var(--lime)] text-[var(--ink)] grid place-items-center font-black">
            M
          </span>
          Mehak Vishwakarma · How I work
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Contact / CTA ---------- */
const Contact = () => (
  <section id="contact" className="bg-[var(--paper)] py-24 md:py-32" data-testid="contact-section">
    <div className="container-x">
      <div className="relative overflow-hidden rounded-[36px] text-white p-10 md:p-16"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 110%, #6fc5ec 0%, #2f9bcf 22%, #1a73b8 45%, #0e4f96 72%, #093a72 100%)",
        }}
      >
        <div className="hero-grain" />
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-wider mb-6 font-light">
              <Zap className="h-3 w-3 text-[var(--lime)]" />
              Limited slots each month
            </div>
            <h2 className="font-display font-medium text-4xl md:text-6xl leading-[1.02]">
              If you're building something{" "}
              <span className="font-serif-italic font-thin text-[var(--lime)]">ambitious,</span>{" "}
              let's talk.
            </h2>
            <p className="mt-6 text-white/85 max-w-xl text-[15px] leading-relaxed font-light">
              30 minute call. No pitch deck. We'll figure out the sharpest lever for your next 90 days (content, research, brand, or a custom AI tool) and whether I'm the right person to build it.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noreferrer"
                className="pill-lime inline-flex items-center gap-2 px-7 py-4 text-[15px]"
                data-testid="contact-book-call-btn"
              >
                <Calendar className="h-4 w-4" />
                Book a Call
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="pill-ghost inline-flex items-center gap-2 px-7 py-4 text-[15px] font-light"
                data-testid="contact-email-btn"
              >
                <Mail className="h-4 w-4" />
                {EMAIL}
              </a>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md p-6">
              <div className="text-[11px] uppercase tracking-wider text-white/65 mb-4 font-light">
                What you'll leave with
              </div>
              <ul className="space-y-3 text-[15px] font-light">
                {[
                  "One sharp diagnosis of your biggest gap",
                  "A concrete next-step (content, brand or AI)",
                  "Honest answer: do you need me, or not",
                ].map((x, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-5 w-5 text-[var(--lime)] flex-shrink-0 mt-0.5" />
                    <span className="text-white/95">{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Footer ---------- */
const Footer = () => (
  <footer className="bg-[var(--ink)] text-white/80 py-14" data-testid="footer">
    <div className="container-x">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full bg-[var(--lime)] grid place-items-center text-[var(--ink)] font-black">
              M
            </span>
            <span className="font-display text-xl font-medium text-white">Mehak</span>
          </div>
          <p className="mt-5 text-sm text-white/60 max-w-md leading-relaxed font-light">
            Founder's operator. Research, content, branding and custom AI tools. Built so you stop drowning in busywork and start compounding what matters.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-wider text-white/45 mb-4 font-light">
            Elsewhere
          </div>
          <ul className="space-y-2.5 text-sm font-light">
            <li>
              <a href={LINKEDIN} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 link-under" data-testid="footer-linkedin">
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
            </li>
            <li>
              <a href={X_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 link-under" data-testid="footer-x">
                <Twitter className="h-4 w-4" /> X / Twitter
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-2 link-under" data-testid="footer-email">
                <Mail className="h-4 w-4" /> {EMAIL}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-wider text-white/45 mb-4 font-light">
            Start
          </div>
          <a href={CALENDLY} target="_blank" rel="noreferrer" className="pill-lime inline-flex items-center gap-2 px-5 py-3 text-sm" data-testid="footer-book-call-btn">
            Book a Call
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[11px] text-white/40 font-light">
        <div>© {new Date().getFullYear()} Mehak Vishwakarma. All rights reserved.</div>
        <div>Built by a human. Not AI slop.</div>
      </div>
    </div>
  </footer>
);

/* ---------- Page ---------- */
const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <Marquee />
    <Services />
    <Work />
    <About />
    <Pull />
    <Contact />
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
